import * as React from 'react'
import {
  Tab as RawTab,
  Tabs as RawTabs,
  TabProps,
  TabsProps,
  Theme,
  createStyles,
  withStyles,
  WithStyles,
} from '@zn-material-ui'
import classnames from 'classnames'
interface ITabProps {
  label: string
  key: string
}

interface ITabsProps extends Partial<TabsProps> {
  tabProps?: TabProps
  tabs: ITabProps[]
  showShadow?: boolean
  align?: 'flex-start' | 'flex-center'
  onTabChange: (value: string) => void
}

const Tabs = (props: ITabsProps & WithStyles<typeof styles>) => {
  const {
    tabProps,
    tabs,
    classes,
    showShadow = true,
    align = 'flex-start',
    onTabChange,
    ...rest
  } = props
  const val = tabs.findIndex(v => v.key === rest.defaultValue)
  const [value, setValue] = React.useState((val !== -1 && val) || 0)
  const onChange = (event: React.ChangeEvent, index: number) => {
    setValue(index)
    onTabChange(tabs[index].key)
  }
  React.useEffect(() => {
    const newIndex = tabs.findIndex(v => v.key === rest.defaultValue)
    if (newIndex !== value && newIndex !== -1) {
      setValue(newIndex)
    }
  }, [rest.defaultValue, tabs])
  return (
    <RawTabs
      value={value}
      onChange={onChange}
      classes={{
        root: classnames(
          classes.root,
          (showShadow && classes.shadowTabBar) || ''
        ),
        indicator: classes.barIndicator,
        flexContainer: classnames(classes.flexContainer, classes[align]),
      }}
      {...rest}
    >
      {tabs.map((tab: ITabProps) => (
        <RawTab
          label={tab.label}
          key={tab.key}
          {...tabProps}
          classes={{
            ...tabProps?.classes,
            selected: classnames(classes.selected, tabProps?.classes?.selected),
            root: classnames(classes.tabRoot, tabProps?.classes?.root),
          }}
        />
      ))}
    </RawTabs>
  )
}

const styles = (theme: Theme) =>
  createStyles({
    flexContainer: {
      margin: 'auto',
    },
    'flex-start': {
      justifyContent: 'start',
    },
    'flex-center': {
      justifyContent: 'center',
    },
    shadowTabBar: {
      boxShadow: '0 1px 1px 0 rgba(0,0,0,0.30) !important',
    },
    root: {
      height: 50,
      boxShadow: 'inset 0 -1px 0 0 #E5E5E5',
      background: theme.palette.secondary.main,
      display: 'flex',
      justifyContent: 'center',
      '& button': {
        minWidth: '70px !important',
        marginBottom: 2,
        '& span:nth-child(2)': {
          display: 'none',
        },
        '&:hover': {
          color: theme.palette.action.hover,
          opacity: 1,
          fontWeight: 'bold',
        },
      },
    },
    barIndicator: {
      backgroundColor: theme.palette.primary.main,
      height: '4px',
    },
    labelContainer: {
      padding: `0px !important`,
    },
    selected: {
      color: `${theme.palette.action.selected} !important`,
      fontWeight: 'bold',
    },
    tabRoot: {
      textTransform: 'initial !important' as 'initial',
      color: theme.palette.text.primary,
      fontSize: '16px',
      letterSpacing: '0',
      textAlign: 'center',
      '&:first-child': {
        marginLeft: 0,
      },
    },
  })

export default withStyles(styles)(Tabs)
